import * as React from 'react';
import { useMemo } from 'react';

import { EntityMetadataModel, EntityPropertyMetadataModel, ParameterMetadata, PrimitiveType } from '../../../../api/api';
import { ExpressionModel } from '../../Models/ExpressionModel';
import { ExpressionTypes } from '../../Models/ExpressionTypes';
import { ObjectExpressionModel } from '../../Models/ObjectExpressionModel';
import { IVariableScope } from '../../Models/VariableScopes/IVariableScope';
import ExpressionObjectNestedPlaceholder from './ExpressionObjectNestedPlaceholder';

export type ExpressionPlaceholderCollectionObjectMapInlineProps = {
	expression: ExpressionModel | undefined;
	onExpressionChange?: (expression: ExpressionModel | undefined) => void;
	parameterMetadata: ParameterMetadata;
	variableScope: IVariableScope | undefined;
	expectedObjectType: EntityMetadataModel;
};

export default function ExpressionPlaceholderCollectionObjectMapInline({
	expression,
	onExpressionChange,
	parameterMetadata,
	variableScope,
	expectedObjectType,
}: ExpressionPlaceholderCollectionObjectMapInlineProps) {
	const collectionObjectProperty = useMemo(() => {
		return {
			name: parameterMetadata.name,
			displayName: parameterMetadata.displayName,
			description: parameterMetadata.description,
			type: PrimitiveType.Object,
			isComplex: true,
			isMultiple: true,
			isRequired: parameterMetadata.isRequired ?? false,
			referenceEntity: expectedObjectType,
		} as EntityPropertyMetadataModel;
	}, [expectedObjectType, parameterMetadata.description, parameterMetadata.displayName, parameterMetadata.isRequired, parameterMetadata.name]);

	const collectionObjectContainerType = useMemo(() => {
		return {
			id: `${parameterMetadata.name}CollectionWrapper`,
			name: parameterMetadata.displayName || parameterMetadata.name,
			properties: [collectionObjectProperty],
		} as EntityMetadataModel;
	}, [collectionObjectProperty, parameterMetadata.displayName, parameterMetadata.name]);

	const collectionObjectExpression = useMemo(() => {
		if (!expression) {
			return undefined;
		}

		return {
			Type: ExpressionTypes.Object,
			Properties: {
				[collectionObjectProperty.name]: expression,
			},
		} as ObjectExpressionModel;
	}, [collectionObjectProperty.name, expression]);

	return (
		<ExpressionObjectNestedPlaceholder
			property={collectionObjectProperty}
			expression={collectionObjectExpression}
			onExpressionChange={(updatedObjectExpression) => {
				onExpressionChange?.(updatedObjectExpression?.Properties?.[collectionObjectProperty.name]);
			}}
			expectedObjectType={collectionObjectContainerType}
			variableScope={variableScope}
		/>
	);
}
